// Shared workflow UI atoms: purpose-built for a high-volume production floor — big patient names,
// monospace group IDs, colour-coded stage bars, loud overdue/urgent states, minimal chrome.
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { WF_STAGE_ROLES as R, formatDate, formatDateTime, type WfCase, type WfPriority, type WfReqStatus, WF_APPROVAL_LABELS, WF_PRIORITY_LABELS, WF_REQ_LABELS } from '@alignova/shared';
import { useWfConfig } from './workflowApi.ts';

export const inputCls = 'w-full rounded border border-line bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent/40';
// Horizontal form layout: on desktop the label sits in a fixed left column with the control beside
// it, so a long form scans as aligned rows rather than a stack of ragged blocks. Stacks on mobile.
export const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-muted md:mb-0 md:pt-2';
/** Put this on any container of <Field>s: one field per row, so the label column stays aligned. */
export const formGridCls = 'grid grid-cols-1 gap-x-4 gap-y-3';

export function Field({
  label,
  children,
  hint,
  className = '',
  stacked = false,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
  className?: string;
  /** Label above the control instead of beside it — for rows that pack several fields side by side. */
  stacked?: boolean;
}) {
  if (stacked) {
    return (
      <label className={`block ${className}`}>
        <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted">{label}</span>
        {children}
        {hint && <span className="mt-1 block text-xs text-muted">{hint}</span>}
      </label>
    );
  }
  return (
    <label className={`block md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4 ${className}`}>
      <span className={labelCls}>{label}</span>
      <span className="block min-w-0">
        {children}
        {hint && <span className="mt-1 block text-xs text-muted">{hint}</span>}
      </span>
    </label>
  );
}

export function Modal({ open, onClose, title, children, wide = false, footer }: { open: boolean; onClose: () => void; title: string; children: ReactNode; wide?: boolean; footer?: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-modal flex items-end justify-center overflow-y-auto bg-navy/60 p-0 sm:items-center sm:p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`w-full ${wide ? 'max-w-4xl' : 'max-w-2xl'} rounded-lg border border-line bg-surface shadow-level-2`}>
        <div className="flex items-center justify-between border-b border-line px-5 py-3 max-h-[92dvh] rounded-t-2xl sm:rounded-lg">
          <h2 className="text-base font-semibold text-ink">{title}</h2>
          <button className="rounded px-2 py-1 text-muted hover:bg-surface-2" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="max-h-[70dvh] overflow-y-auto px-4 py-4 sm:px-5" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

export function Btn({ tone = 'primary', className = '', ...p }: React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'primary' | 'ghost' | 'danger' | 'accent' | 'subtle' }) {
  const t = {
    primary: 'bg-primary text-primary-fg hover:opacity-90',
    accent: 'bg-accent text-accent-fg hover:opacity-90',
    ghost: 'border border-line bg-transparent text-ink hover:bg-surface-2',
    subtle: 'bg-surface-2 text-ink hover:bg-line',
    danger: 'bg-status-urgent-fg text-white hover:opacity-90',
  }[tone];
  return <button className={`inline-flex items-center justify-center gap-1 whitespace-nowrap rounded px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${t} ${className}`} {...p} />;
}

export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <div className="rounded border border-status-urgent-fg/30 bg-status-urgent-bg px-3 py-2 text-sm text-status-urgent-fg">{children}</div>;
}

export function StagePill({ stageKey, small = false }: { stageKey: string; small?: boolean }) {
  const { stageMap } = useWfConfig();
  const s = stageMap.get(stageKey);
  const color = s?.color ?? '#64748B';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 ${small ? 'py-0 text-[11px]' : 'py-0.5 text-xs'} font-medium`} style={{ borderColor: color, color, background: `${color}14` }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      {s?.name ?? stageKey}
    </span>
  );
}

/** A solid tag for a case that has left the lab, so it never reads as live work in a list. */
const DONE_TAGS: Record<string, { label: string; cls: string }> = {
  dispatched: { label: '✓ Dispatched', cls: 'bg-blue-600 text-white' },
  delivered_completed: { label: '✓ Completed', cls: 'bg-green-600 text-white' },
  case_cancelled: { label: 'Cancelled', cls: 'bg-slate-500 text-white' },
};
export function DoneTag({ stageKey }: { stageKey: string }) {
  const t = DONE_TAGS[stageKey];
  if (!t) return null;
  return <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${t.cls}`}>{t.label}</span>;
}

const PRIO: Record<WfPriority, string> = { normal: 'bg-status-pending-bg text-status-pending-fg', urgent: 'bg-amber-100 text-amber-800', emergency: 'bg-status-urgent-bg text-status-urgent-fg' };
export function PriorityPill({ p }: { p: WfPriority }) {
  if (p === 'normal') return null;
  // The stored key is still 'emergency'; what a clinic agreed to is "Express".
  return <span className={`rounded px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${PRIO[p]}`}>{WF_PRIORITY_LABELS[p]}</span>;
}

const REQ: Record<WfReqStatus, string> = { not_required: 'text-muted', required: 'text-amber-700 font-semibold', pending: 'text-orange-700 font-semibold', completed: 'text-status-progress-fg font-semibold' };
export function ReqBadge({ label, s }: { label: string; s: WfReqStatus }) {
  if (s === 'not_required') return null;
  return (
    <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[11px]">
      {label}: <span className={REQ[s]}>{WF_REQ_LABELS[s]}</span>
    </span>
  );
}

export function DueBadge({ c }: { c: Pick<WfCase, 'dueDate' | 'dueInDays' | 'isOverdue'> }) {
  if (!c.dueDate) return <span className="text-xs text-muted">No due date</span>;
  const d = c.dueInDays ?? 0;
  let cls = 'text-muted';
  let txt = formatDate(c.dueDate);
  if (c.isOverdue) {
    cls = 'font-bold text-status-urgent-fg';
    txt = `OVERDUE ${Math.abs(d)}d · ${formatDate(c.dueDate)}`;
  } else if (d === 0) {
    cls = 'font-bold text-orange-700';
    txt = `Due TODAY`;
  } else if (d === 1) {
    cls = 'font-semibold text-amber-700';
    txt = 'Due tomorrow';
  } else if (d <= 3) cls = 'font-medium text-amber-700';
  return <span className={`text-xs ${cls}`}>{txt}</span>;
}

export function AgeBadge({ c }: { c: Pick<WfCase, 'daysInStage' | 'hoursInStage' | 'slaBreached'> }) {
  const txt = c.hoursInStage < 24 ? `${Math.round(c.hoursInStage)}h` : `${c.daysInStage}d`;
  return <span className={`rounded px-1.5 py-0.5 font-mono text-[11px] ${c.slaBreached ? 'bg-status-urgent-bg text-status-urgent-fg' : 'bg-surface-2 text-muted'}`} title="Time in current stage">{c.slaBreached ? '⚠ ' : ''}{txt}</span>;
}

/** The workflow card — used on the Kanban board and in queue lists. */
export function CaseCard({ c, onAction, compact = false, selected, onSelect, draggable, onDragStart }: {
  c: WfCase;
  onAction?: (action: string, c: WfCase) => void;
  compact?: boolean;
  selected?: boolean;
  onSelect?: (id: string, checked: boolean) => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent, c: WfCase) => void;
}) {
  const { optionLabel, stageMap } = useWfConfig();
  const stage = stageMap.get(c.stageKey);
  const urgentBorder = c.priority === 'emergency' ? 'border-l-status-urgent-fg' : c.priority === 'urgent' ? 'border-l-amber-500' : c.isOverdue ? 'border-l-status-urgent-fg/60' : 'border-l-transparent';
  const note = c.additionalNotes || c.notes;
  return (
    <div
      draggable={draggable}
      onDragStart={(e) => onDragStart?.(e, c)}
      className={`group relative rounded-md border border-line border-l-4 bg-surface p-3 shadow-level-1 transition hover:shadow-level-2 ${urgentBorder} ${selected ? 'ring-2 ring-accent' : ''} ${draggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {onSelect && <input type="checkbox" checked={!!selected} onChange={(e) => onSelect(c.id, e.target.checked)} onClick={(e) => e.stopPropagation()} className="h-4 w-4" />}
            <Link to={`/workflow/cases/${c.id}`} className="truncate text-[15px] font-semibold tracking-tight text-ink hover:underline">
              {c.patientName}
            </Link>
            <DoneTag stageKey={c.stageKey} />
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted">
            <span className="font-mono font-semibold text-ink">{c.groupId ?? '-'}</span>
            <span className="font-mono">{c.caseNumber}</span>
            <span>{c.caseTypeRaw ?? c.caseTypes.map((t) => optionLabel('case_type', t)).join(', ')}</span>
            {c.receivedThrough && <span>· {optionLabel('received_through', c.receivedThrough)}</span>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <PriorityPill p={c.priority} />
          <AgeBadge c={c} />
        </div>
      </div>
      {!compact && (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {stage && !stage.onBoard && <StagePill stageKey={c.stageKey} small />}
            <ReqBadge label="IPR" s={c.iprStatus} />
            <ReqBadge label="Etching" s={c.etchingStatus} />
            {c.approvalState !== 'none' && <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[11px]">PT: {WF_APPROVAL_LABELS[c.approvalState]}</span>}
            {c.reprintCount > 0 && <span className="rounded bg-status-urgent-bg px-1.5 py-0.5 text-[11px] text-status-urgent-fg">Reprint ×{c.reprintCount}</span>}
            {c.holdReason && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-800">{optionLabel('hold_reason', c.holdReason)}</span>}
          </div>
          {note && <p className="mt-2 line-clamp-2 text-xs text-ink/80" title={note}>{note}</p>}
          <div className="mt-2 flex items-center justify-between text-xs">
            <DueBadge c={c} />
            <span className="truncate text-muted">{c.assignedToName ?? 'Unassigned'}</span>
          </div>
        </>
      )}
      {onAction && (
        <div className="mt-2 flex flex-wrap gap-1 border-t border-line pt-2 opacity-80 group-hover:opacity-100">
          <MiniBtn onClick={() => onAction('open', c)}>Open</MiniBtn>
          <MiniBtn onClick={() => onAction('move', c)}>Move</MiniBtn>
          {c.stageKey === 'on_hold' ? <MiniBtn onClick={() => onAction('resume', c)}>Resume</MiniBtn> : !stage?.isTerminal && <MiniBtn onClick={() => onAction('hold', c)}>Hold</MiniBtn>}
          <MiniBtn onClick={() => onAction('note', c)}>Note</MiniBtn>
          <MiniBtn onClick={() => onAction('assign', c)}>Assign</MiniBtn>
          {c.stageKey === R.doctorApproval && <MiniBtn onClick={() => onAction('approve', c)}>Approve</MiniBtn>}
          {stage?.group === 'review' && <MiniBtn onClick={() => onAction('review', c)}>Review</MiniBtn>}
          {stage?.group === 'production' && <MiniBtn onClick={() => onAction('reprint', c)}>Reprint</MiniBtn>}
          {stage?.key === R.packaging && <MiniBtn onClick={() => onAction('dispatch', c)}>Dispatch</MiniBtn>}
        </div>
      )}
    </div>
  );
}
export function MiniBtn(p: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button {...p} className={`rounded border border-line px-2 py-0.5 text-[11px] font-medium text-ink hover:bg-surface-2 ${p.className ?? ''}`} onClick={(e) => { e.stopPropagation(); p.onClick?.(e); }} />;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="rounded-md border border-dashed border-line px-4 py-8 text-center text-sm text-muted">{children}</div>;
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: Array<{ key: T; label: string; count?: number }>; value: T; onChange: (t: T) => void }) {
  return (
    <div className="flex flex-wrap gap-1 border-b border-line">
      {tabs.map((t) => (
        <button key={t.key} onClick={() => onChange(t.key)} className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${value === t.key ? 'border-accent text-ink' : 'border-transparent text-muted hover:text-ink'}`}>
          {t.label}
          {t.count !== undefined && <span className="ml-1.5 rounded-full bg-surface-2 px-1.5 text-[11px] text-muted">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/**
 * A single number and what it counts.
 *
 * Twelve of these sit side by side, so the frame has to be quiet: a hairline, no shadow at rest,
 * and colour ONLY when the number means something is wrong. Twelve tinted boxes is a fruit salad —
 * one tinted box among eleven plain ones is a signal. The figure itself carries the weight: large,
 * tightly tracked, tabular so a column of them lines up.
 *
 * `size="lg"` is for the handful of headline metrics; the rest stay compact.
 */
export function StatTile({
  label,
  value,
  tone = 'neutral',
  onClick,
  sub,
  size = 'md',
}: {
  label: string;
  value: number | string;
  tone?: 'neutral' | 'bad' | 'warn' | 'good' | 'accent';
  onClick?: () => void;
  sub?: string;
  size?: 'md' | 'lg';
}) {
  // A zero is never an alarm, whatever tone the caller asked for: "0 overdue" is good news.
  const quiet = value === 0 || value === '0';
  const active = quiet ? 'neutral' : tone;
  const frames = {
    neutral: 'border-line',
    bad: 'border-status-urgent-fg/30 bg-status-urgent-bg/30',
    warn: 'border-status-warn-fg/25 bg-status-warn-bg/40',
    good: 'border-status-progress-fg/25 bg-status-progress-bg/30',
    accent: 'border-accent/30 bg-accent/[0.04]',
  }[active];
  const figure = {
    neutral: 'text-ink',
    bad: 'text-status-urgent-fg',
    warn: 'text-status-warn-fg',
    good: 'text-status-progress-fg',
    accent: 'text-ink',
  }[active];
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`group rounded-xl border bg-surface text-left transition-all duration-200 ${
        size === 'lg' ? 'p-4' : 'p-3.5'
      } ${onClick ? 'hover:-translate-y-px hover:border-accent/40 hover:shadow-level-2' : 'cursor-default'} ${frames}`}
    >
      <div className="text-[12px] font-medium text-muted">{label}</div>
      <div
        className={`tabnum mt-1.5 font-semibold leading-none tracking-tight ${figure} ${
          size === 'lg' ? 'text-[2.25rem]' : 'text-[1.75rem]'
        }`}
      >
        {value}
      </div>
      {sub && <div className="mt-1.5 text-xs text-muted">{sub}</div>}
    </button>
  );
}

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function fmtDT(iso: string | null | undefined): string {
  return iso ? formatDateTime(iso) : '-';
}
export function fmtD(iso: string | null | undefined): string {
  return iso ? formatDate(iso) : '-';
}
export function hours(h: number): string {
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${Math.round(h)}h`;
  return `${(h / 24).toFixed(1)}d`;
}
